import type { InstancedMesh, Matrix4, BufferGeometry } from 'three';
import type { Rng } from '../../core/rng';
import type { PaletteModel } from '../../data/types';
import type { Vec2, Vec3, Ring } from '../../geo/types';
import type { Animated } from '../types';
import {
  createMeshBuilder,
  type ExaggerationStyle,
  type MeshBuilder,
  type VertexStyle,
} from '../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../mesh/attributes';
import { SURFACE_TO_CLASS } from '../../render/surfaceShading';
import { STYLE_CONSTANTS } from '../../render/painterProjection';
import { directionFromHeading } from '../../core/coords';
import {
  POST_BODY,
  SAIL_RADIUS_MIN,
  STOCK_HALF_WIDTH,
  windmillForm,
  type WindmillForm,
  type WindmillKind,
  type WindmillSetting,
} from './windmillForms';
import {
  beam,
  frustum,
  outwardPolygon,
  outwardQuad,
  rectangle,
  regularPolygon,
  twoSidedQuad,
  walls,
} from './solidParts';

/* PLAN §5: a post mill is a trestle, a post and a 4 × 3 × 5 m body at 4 m (windmillForms.ts). */
const POST_ROOF_RISE = 2;
const POST = { radius: 0.3, sides: 8 } as const;
const CROSS_TREE = { reach: 2.2, halfWidth: 0.15 } as const;
const QUARTER_BAR = { foot: 1.9, head: 2.4, halfWidth: 0.1 } as const;
const TOWER_MILL_HEIGHT = windmillForm('tower', 'ground').shaft.height;
const TOWER_SIDES = 8;
const MOUND_SIDES = 8;
const CAP = { radiusBottom: 2.3, radiusTop: 0.5, height: 3 } as const;
const WINDSHAFT_HALF_WIDTH = 0.2;

const SAIL_RADIUS_MAX = 11;
const CLOTH = { inner: 1.8, fromTip: 0.2, width: 1.6, standOff: 0.05 } as const;
const SAIL_ROTATION_SPEED_BASE = 1.2;
const SAIL_SPEED_FACTOR = { min: 0.8, max: 1.2 } as const;
const OBSTACLE_SIZE = 5;
/*
 * A wall mill rises with the tower under it in the painter view: the fortifications draw a tower
 * at the whole vertical exaggeration from the ground up (towers.ts), so its floor lands at its
 * height times the exaggeration, and the mill's own heights, taken from the ground, land with it.
 */
const WALL_EXAGGERATION: ExaggerationStyle = { base: 0, share: 1 };
/*
 * The painting draws the ground mills about twice as tall as the painter camera sees them. Painted
 * hub height plus sail radius against the rendered 20.5 m mill, foot to top (frame px): the south
 * mills at (306, 415) 44 against 19.2, (259, 469) 43 against 18.9 and (197, 513) 51 against 18.3,
 * the pair before the Heiligewegspoort at (733, 236) 30 against 18.1 and (758, 236) 28 against 18.0;
 * the Haarlemmerdijk mill shows only a sail over its mound. The ratios run from 1.56 to 2.79. All
 * ground mills are post mills built from one template, so they take one share: the one that makes
 * their height the ratios' geometric mean, 2.07, 26% short of the nearest south mill and 33% over
 * the second Heiligeweg mill. Walk and fly views take no exaggeration.
 */
const GROUND_MILL_SCALE = 2.07;
export const GROUND_MILL_SHARE =
  (GROUND_MILL_SCALE - 1) / (STYLE_CONSTANTS.verticalExaggeration - 1);
const GROUND_EXAGGERATION: ExaggerationStyle = { base: 0, share: GROUND_MILL_SHARE };

export type { WindmillKind, WindmillSetting } from './windmillForms';

export interface WindmillInstance {
  readonly index: number;
  readonly baseX: number;
  readonly baseY: number;
  readonly baseZ: number;
  readonly heading: number;
  readonly rotationSpeed: number;
  readonly initialAngle: number;
}

export function createWindmillSailAnimator(
  id: string,
  mesh: InstancedMesh,
  instances: readonly WindmillInstance[],
  tempMatrix: Matrix4
): Animated {
  const cos = Math.cos;
  const sin = Math.sin;

  return {
    id,
    update(timeSeconds: number): void {
      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i]!;
        const sailAngle = inst.initialAngle + timeSeconds * inst.rotationSpeed;
        const cosH = cos(inst.heading);
        const sinH = sin(inst.heading);
        const cosS = cos(sailAngle);
        const sinS = sin(sailAngle);

        const te = tempMatrix.elements;
        te[0] = cosH;
        te[1] = 0;
        te[2] = -sinH;
        te[3] = 0;
        te[4] = sinH * sinS;
        te[5] = cosS;
        te[6] = cosH * sinS;
        te[7] = 0;
        te[8] = sinH * cosS;
        te[9] = -sinS;
        te[10] = cosH * cosS;
        te[11] = 0;
        te[12] = inst.baseX;
        te[13] = inst.baseY;
        te[14] = inst.baseZ;
        te[15] = 1;

        mesh.setMatrixAt(inst.index, tempMatrix);
      }

      mesh.instanceMatrix.needsUpdate = true;
    },
  };
}

export function drawSailSpeed(rng: Rng): number {
  return SAIL_ROTATION_SPEED_BASE * rng.range(SAIL_SPEED_FACTOR.min, SAIL_SPEED_FACTOR.max);
}

export function buildWindmillObstacle(position: Vec2): Ring {
  const half = OBSTACLE_SIZE / 2;
  return [
    [position[0] - half, position[1] - half],
    [position[0] + half, position[1] - half],
    [position[0] + half, position[1] + half],
    [position[0] - half, position[1] + half],
  ];
}

/* Axle height above the mill's base: the height of the sail instances' rotation centre over it. */
export function windmillSailHeight(
  kind: WindmillKind,
  setting: WindmillSetting = 'ground'
): number {
  return windmillForm(kind, setting).hubHeight;
}

export function windmillSailRadius(
  kind: WindmillKind,
  setting: WindmillSetting = 'ground'
): number {
  return windmillForm(kind, setting).sailRadius;
}

/* The ground under the disc the sails sweep, in layout coordinates. */
export function windmillSweepFootprint(
  position: Vec2,
  heading: number,
  kind: WindmillKind,
  setting: WindmillSetting = 'ground'
): Ring {
  const { stockX, sailRadius } = windmillForm(kind, setting);
  const ahead = directionFromHeading(heading);
  const right: Vec2 = [ahead[1], -ahead[0]];
  const back = stockX - STOCK_HALF_WIDTH;
  const front = stockX + STOCK_HALF_WIDTH + CLOTH.standOff;
  const at = (along: number, across: number): Vec2 => [
    position[0] + ahead[0] * along + right[0] * across,
    position[1] + ahead[1] * along + right[1] * across,
  ];
  return [
    at(back, -sailRadius),
    at(front, -sailRadius),
    at(front, sailRadius),
    at(back, sailRadius),
  ];
}

interface TemplateStyle {
  readonly palette: PaletteModel;
  readonly edgeId: number;
  readonly seed: number;
  readonly setting: WindmillSetting;
}

function paletteStyle(
  template: TemplateStyle,
  surface: SurfaceKind,
  extent: readonly [number, number] = [0, 0]
): VertexStyle {
  const { palette, edgeId, seed, setting } = template;
  const strokeAngle = surface === SURFACE.wood ? Math.PI / 2 : 0;
  const color = palette[SURFACE_TO_CLASS[surface]].median.linear;
  const style = { color, surface, edgeId, seed, strokeAngle, extent };
  const exaggeration = setting === 'wall' ? WALL_EXAGGERATION : GROUND_EXAGGERATION;
  return { ...style, exaggeration };
}

function addTrestleAndPost(builder: MeshBuilder, wood: VertexStyle, standing: number): void {
  const { reach, halfWidth } = CROSS_TREE;
  const y = standing + halfWidth;
  beam(builder, wood, [-reach, y, 0], [reach, y, 0], halfWidth);
  beam(builder, wood, [0, y, -reach], [0, y, reach], halfWidth);
  const top = standing + 2 * halfWidth;
  const feet: readonly Vec3[] = [
    [QUARTER_BAR.foot, top, 0],
    [-QUARTER_BAR.foot, top, 0],
    [0, top, QUARTER_BAR.foot],
    [0, top, -QUARTER_BAR.foot],
  ];
  for (const foot of feet) {
    const head: Vec3 = [
      Math.sign(foot[0]) * POST.radius,
      standing + QUARTER_BAR.head,
      Math.sign(foot[2]) * POST.radius,
    ];
    beam(builder, wood, foot, head, QUARTER_BAR.halfWidth);
  }
  const post = { sides: POST.sides, radiusBottom: POST.radius, radiusTop: POST.radius };
  /* Solids close on top, even where hidden under the body floor; their feet stand on the ground. */
  frustum(
    builder,
    wood,
    { ...post, y0: standing, y1: standing + POST_BODY.base },
    { top: true, bottom: false }
  );
}

function addPostMillBody(
  builder: MeshBuilder,
  wood: VertexStyle,
  thatch: VertexStyle,
  form: WindmillForm
): void {
  const halfL = POST_BODY.length / 2;
  const halfW = POST_BODY.width / 2;
  const floor = (form.mound?.height ?? 0) + POST_BODY.base;
  const eaves = floor + POST_BODY.height;
  const ridge = eaves + POST_ROOF_RISE;
  const inside: Vec3 = [0, eaves, 0];
  walls(builder, () => wood, rectangle(halfL, halfW), floor, eaves, { floor: true });
  for (const x of [-halfL, halfL]) {
    const gable: Vec3[] = [
      [x, eaves, -halfW],
      [x, eaves, halfW],
      [x, ridge, 0],
    ];
    outwardPolygon(builder, wood, gable, inside);
  }
  for (const z of [-halfW, halfW]) {
    const slope: readonly [Vec3, Vec3, Vec3, Vec3] = [
      [-halfL, eaves, z],
      [halfL, eaves, z],
      [halfL, ridge, 0],
      [-halfL, ridge, 0],
    ];
    outwardQuad(builder, thatch, slope, inside);
  }
  const hub = form.hubHeight;
  const shaftEnd = form.stockX - STOCK_HALF_WIDTH;
  beam(builder, wood, [halfL - 0.8, hub, 0], [shaftEnd, hub, 0], WINDSHAFT_HALF_WIDTH);
}

/* The earth a wall post mill's trestle stands on; the platform's floor closes it underneath. */
function addMound(builder: MeshBuilder, earth: VertexStyle, form: WindmillForm): void {
  if (!form.mound) return;
  const { height, radiusBottom, radiusTop } = form.mound;
  const shape = { sides: MOUND_SIDES, radiusBottom, radiusTop, y0: 0, y1: height };
  frustum(builder, earth, shape, { top: true, bottom: false });
}

function addTowerMill(builder: MeshBuilder, template: TemplateStyle, form: WindmillForm): void {
  /* The painting (1538, scan px 5640-5695 x 2430-2510) paints the wall's tower mill grey stone. */
  const onWall = template.setting === 'wall';
  const masonry = paletteStyle(template, onWall ? SURFACE.stone : SURFACE.plaster);
  const thatch = paletteStyle(template, SURFACE.roofThatch);
  const wood = paletteStyle(template, SURFACE.wood);
  const { shaft } = form;
  const tower = {
    sides: TOWER_SIDES,
    radiusBottom: shaft.radiusBottom,
    radiusTop: shaft.radiusTop,
  };
  frustum(builder, masonry, { ...tower, y0: 0, y1: shaft.height }, { top: true, bottom: false });
  const cap = { sides: TOWER_SIDES, ...CAP, y0: shaft.height, y1: shaft.height + CAP.height };
  frustum(builder, thatch, cap, { top: true, bottom: false });
  /* The cap overhangs the tower: its underside is the timber curb, since thatch never faces down. */
  const curb = regularPolygon(CAP.radiusBottom, TOWER_SIDES).map((p): Vec3 => [p[0], cap.y0, p[1]]);
  outwardPolygon(builder, wood, curb, [0, (cap.y0 + cap.y1) / 2, 0]);
  const hub = form.hubHeight;
  const shaftEnd = form.stockX - STOCK_HALF_WIDTH;
  beam(builder, wood, [1, hub, 0], [shaftEnd, hub, 0], WINDSHAFT_HALF_WIDTH);
}

/* Every colour comes from the palette class of its surface (PLAN §4), so no colour is chosen here. */
export function buildWindmillBody(
  kind: WindmillKind,
  palette: PaletteModel,
  edgeId: number,
  seed: number,
  setting: WindmillSetting = 'ground'
): BufferGeometry {
  const builder = createMeshBuilder();
  const template: TemplateStyle = { palette, edgeId, seed, setting };
  const form = windmillForm(kind, setting);
  if (kind === 'tower') {
    addTowerMill(builder, template, form);
    return builder.build();
  }
  const wood = paletteStyle(template, SURFACE.wood, [POST_BODY.length, POST_BODY.height]);
  addMound(builder, paletteStyle(template, SURFACE.quayEarth), form);
  addTrestleAndPost(builder, wood, form.mound?.height ?? 0);
  addPostMillBody(builder, wood, paletteStyle(template, SURFACE.roofThatch), form);
  return builder.build();
}

/* Two crossed stocks through the hub and four sail cloths, turning in the plane in front of the body. */
export function buildWindmillSails(
  kind: WindmillKind,
  palette: PaletteModel,
  edgeId: number,
  seed: number,
  setting: WindmillSetting = 'ground'
): BufferGeometry {
  const builder = createMeshBuilder();
  const template: TemplateStyle = { palette, edgeId, seed, setting };
  const { stockX: x, sailRadius: r } = windmillForm(kind, setting);
  const outer = r - CLOTH.fromTip;
  const wood = paletteStyle(template, SURFACE.wood);
  const sail = paletteStyle(template, SURFACE.sail, [CLOTH.width, outer - CLOTH.inner]);
  beam(builder, wood, [x, -r, 0], [x, r, 0], STOCK_HALF_WIDTH);
  beam(builder, wood, [x + 0.01, 0, -r], [x + 0.01, 0, r], STOCK_HALF_WIDTH);

  const clothX = x + STOCK_HALF_WIDTH + CLOTH.standOff;
  for (let k = 0; k < 4; k++) {
    const along: Vec2 = [Math.cos((k * Math.PI) / 2), Math.sin((k * Math.PI) / 2)];
    const side: Vec2 = [-along[1], along[0]];
    const at = (radius: number, offset: number): Vec3 => [
      clothX,
      along[0] * radius + side[0] * offset,
      along[1] * radius + side[1] * offset,
    ];
    const near = STOCK_HALF_WIDTH;
    const far = STOCK_HALF_WIDTH + CLOTH.width;
    twoSidedQuad(builder, sail, [
      at(CLOTH.inner, near),
      at(outer, near),
      at(outer, far),
      at(CLOTH.inner, far),
    ]);
  }
  return builder.build();
}

export { POST_BODY, TOWER_MILL_HEIGHT, SAIL_RADIUS_MIN, SAIL_RADIUS_MAX, SAIL_ROTATION_SPEED_BASE };
